import {
  COMPLIANCE_DOCUMENT_STATUS,
  ComplianceDataType,
  ComplianceDocumentMetadataTypeFE,
  MapedPoliciesDataType,
  PoliciesDataType,
} from 'domain/shared/compliance';
import { COMPLIANCE_DOCUMENT_BASE_STATUS_VIEW, OUTDATED_UNPUBLISHED_DOCUMENT_INFO_MESSAGE } from './constants';
import {
  ComplianceAudienceEmployee,
  ComplianceCoverageEmployee,
  ComplianceCoverageStatus,
  ComplianceCoverageStatusFallback,
  ComplianceDocumentMatchParams,
  ComplianceDocumentStatusView,
  ComplianceDocumentStatusViewOptions,
} from './types';

export const getDocumentStatusView = (
  status?: ComplianceDocumentMetadataTypeFE['status'],
  options: ComplianceDocumentStatusViewOptions = {},
): ComplianceDocumentStatusView => {
  const { isOutdated = false } = options;

  if (isOutdated) {
    if (status === COMPLIANCE_DOCUMENT_STATUS.UNPUBLISHED || !status) {
      return {
        label: 'Outdated',
        color: 'default',
        shouldLoadStatuses: false,
        infoMessage: OUTDATED_UNPUBLISHED_DOCUMENT_INFO_MESSAGE,
      };
    }

    return {
      label: 'Outdated',
      color: 'default',
      shouldLoadStatuses: true,
    };
  }

  return status ? COMPLIANCE_DOCUMENT_BASE_STATUS_VIEW[status] : COMPLIANCE_DOCUMENT_BASE_STATUS_VIEW.UNPUBLISHED;
};

const normalizeComparablePath = (path?: string) => {
  if (!path) {
    return '';
  }

  return path.startsWith('/') ? path.slice(1) : path;
};

export const findMatchingComplianceDocument = (
  complianceDocuments: MapedPoliciesDataType[] = [],
  params: ComplianceDocumentMatchParams,
): Nullable<PoliciesDataType> => {
  const { complianceName, currentDocumentPath, currentDocumentVersion } = params;
  const normalizedVersion = currentDocumentVersion?.toString();
  const normalizedDocumentPath = normalizeComparablePath(currentDocumentPath);
  const hasMatchingParams = !!normalizedDocumentPath || !!complianceName || !!normalizedVersion;

  if (!hasMatchingParams) {
    return null;
  }

  return (
    complianceDocuments.find(complianceDocument => {
      const pathMatches =
        !normalizedDocumentPath || normalizeComparablePath(complianceDocument.policyPath) === normalizedDocumentPath;
      const complianceMatches =
        !complianceName ||
        complianceDocument.complianceName === complianceName ||
        complianceDocument.policyType === complianceName;
      const versionMatches = !normalizedVersion || complianceDocument.policyVersion === normalizedVersion;

      return pathMatches && complianceMatches && versionMatches;
    }) ?? null
  );
};

export const getComplianceCoverageStatus = (complianceDocument: PoliciesDataType): ComplianceCoverageStatus =>
  complianceDocument.isPolicyAccepted ? ComplianceCoverageStatus.SIGNED : ComplianceCoverageStatus.NOT_SIGNED;

export const mapAudienceEmployeesToCoverage = (
  audienceEmployees: ComplianceAudienceEmployee[],
  complianceEmployees: ComplianceDataType[],
  params: ComplianceDocumentMatchParams,
  fallbackStatus: ComplianceCoverageStatusFallback = ComplianceCoverageStatus.NOT_SIGNED,
): ComplianceCoverageEmployee[] => {
  const complianceEmployeesByAlias = new Map(
    (complianceEmployees || []).map(employee => [employee.employeeAlias, employee]),
  );

  return audienceEmployees.map(employee => {
    const complianceEmployee = complianceEmployeesByAlias.get(employee.employeeAlias);
    const complianceDocument = findMatchingComplianceDocument(complianceEmployee?.policies || [], params);

    return {
      ...employee,
      ...(complianceDocument ? { complianceDocument } : {}),
      status: complianceDocument ? getComplianceCoverageStatus(complianceDocument) : fallbackStatus,
    };
  });
};
